export const SYSTEM_NOTIFICATION_TYPES = {
  INTERVIEWS_WITHOUT_NOTES: 'interviews-without-notes',
  PAST_DUE_TASKS: 'past-due-tasks',
  FAILED_SEQUENCE_EMAILS: 'failed-sequence-emails',
  ACTIVE_JOBS_ZERO_CANDIDATES: 'active-jobs-zero-candidates',
  OVERDUE_CLIENT_INVOICES: 'overdue-client-invoices',
  SENDER_ACCOUNTS_SPAM_FAILING: 'sender-accounts-spam-failing',
  SENDER_ACCOUNTS_WARMUP_INCOMPLETE: 'sender-accounts-warmup-incomplete',
  CONTRACTS_AWAITING_CLIENT_SIGNATURE: 'contracts-awaiting-client-signature',
  CONTRACTS_EXPIRING_SOON: 'contracts-expiring-soon',
  PAYMENT_METHOD_EXPIRING_SOON: 'payment-method-expiring-soon',
  MONTHLY_PAYMENT_FAILED: 'monthly-payment-failed',
} as const;

export type SystemNotificationType = (typeof SYSTEM_NOTIFICATION_TYPES)[keyof typeof SYSTEM_NOTIFICATION_TYPES];

const SYSTEM_NOTIFICATION_TYPE_SET = new Set<string>(Object.values(SYSTEM_NOTIFICATION_TYPES));

const FLAG_SYSTEM_NOTIFICATION_TYPES = new Set<SystemNotificationType>([
  SYSTEM_NOTIFICATION_TYPES.PAYMENT_METHOD_EXPIRING_SOON,
  SYSTEM_NOTIFICATION_TYPES.MONTHLY_PAYMENT_FAILED,
]);

export const isSystemNotificationType = (type: string): type is SystemNotificationType =>
  SYSTEM_NOTIFICATION_TYPE_SET.has(type);

export const isFlagSystemNotificationType = (type: string): type is SystemNotificationType =>
  isSystemNotificationType(type) && FLAG_SYSTEM_NOTIFICATION_TYPES.has(type);

export const SYSTEM_NOTIFICATION_HREFS: Partial<Record<SystemNotificationType, string>> = {
  [SYSTEM_NOTIFICATION_TYPES.INTERVIEWS_WITHOUT_NOTES]: '/',
  [SYSTEM_NOTIFICATION_TYPES.PAST_DUE_TASKS]: '/tasks?pastDue=1',
  [SYSTEM_NOTIFICATION_TYPES.FAILED_SEQUENCE_EMAILS]: '/sequences',
  [SYSTEM_NOTIFICATION_TYPES.ACTIVE_JOBS_ZERO_CANDIDATES]: '/jobs?activeWithZeroCandidates=true',
  [SYSTEM_NOTIFICATION_TYPES.OVERDUE_CLIENT_INVOICES]: '/clients/invoices?overdue=true',
  [SYSTEM_NOTIFICATION_TYPES.SENDER_ACCOUNTS_SPAM_FAILING]: '/settings/sender-accounts?spamFailing=true',
  [SYSTEM_NOTIFICATION_TYPES.SENDER_ACCOUNTS_WARMUP_INCOMPLETE]: '/settings/sender-accounts?warmupIncomplete=true',
  [SYSTEM_NOTIFICATION_TYPES.CONTRACTS_AWAITING_CLIENT_SIGNATURE]: '/contracts',
  [SYSTEM_NOTIFICATION_TYPES.CONTRACTS_EXPIRING_SOON]: '/contracts',
};

export const buildSystemNotificationContent = (type: SystemNotificationType, count?: number): string => {
  switch (type) {
    case SYSTEM_NOTIFICATION_TYPES.INTERVIEWS_WITHOUT_NOTES:
      return count === 1 ? '1 Incomplete Interviews.' : `${count} Incomplete Interviews.`;
    case SYSTEM_NOTIFICATION_TYPES.PAST_DUE_TASKS:
      return count === 1 ? '1 Past Due task.' : `${count} Past Due tasks.`;
    case SYSTEM_NOTIFICATION_TYPES.FAILED_SEQUENCE_EMAILS:
      return count === 1 ? '1 failed sequence email.' : `${count} failed sequence emails.`;
    case SYSTEM_NOTIFICATION_TYPES.ACTIVE_JOBS_ZERO_CANDIDATES:
      return count === 1
        ? '1 active job with zero candidates.'
        : `${count} active jobs with zero candidates.`;
    case SYSTEM_NOTIFICATION_TYPES.OVERDUE_CLIENT_INVOICES:
      return count === 1 ? '1 Overdue client invoice.' : `${count} Overdue client invoices.`;
    case SYSTEM_NOTIFICATION_TYPES.SENDER_ACCOUNTS_SPAM_FAILING:
      return count === 1
        ? '1 Sender Account failing spam tests.'
        : `${count} Sender Accounts failing spam tests.`;
    case SYSTEM_NOTIFICATION_TYPES.SENDER_ACCOUNTS_WARMUP_INCOMPLETE:
      return count === 1
        ? '1 Sender Account with incomplete warmup.'
        : `${count} Sender Accounts with incomplete warmup.`;
    case SYSTEM_NOTIFICATION_TYPES.CONTRACTS_AWAITING_CLIENT_SIGNATURE:
      return count === 1
        ? '1 contract awaiting client signature.'
        : `${count} contracts awaiting client signature.`;
    case SYSTEM_NOTIFICATION_TYPES.CONTRACTS_EXPIRING_SOON:
      return count === 1 ? '1 contract expiring soon.' : `${count} contracts expiring soon.`;
    case SYSTEM_NOTIFICATION_TYPES.PAYMENT_METHOD_EXPIRING_SOON:
      return 'Your payment method is expiring soon. Manage billing to update the card before it stops working.';
    case SYSTEM_NOTIFICATION_TYPES.MONTHLY_PAYMENT_FAILED:
      return 'Your monthly payment failed. Manage billing to update the card and retry the charge.';
    default:
      return typeof count === 'number' ? `${count}` : '';
  }
};
